import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Divider,
  DividerProps,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LoginIcon from '@mui/icons-material/Login';

import { TP_COLORS } from '../../../constants';
import { useAppSelector } from '../../../hooks';
import { getAuthState } from '../../../store/AuthSlice';
import { isMobileScreen } from '../../../helper';

const dividerProps: DividerProps = {
  flexItem: true,
  orientation: 'vertical',
  variant: 'middle',
  sx: { margin: '16px', borderWidth: '1px' },
};

const HomeHeader: React.FC = () => {
  const isMobile = isMobileScreen();
  // STORE
  const authState = useAppSelector(getAuthState);

  // STATES
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // BOOLEANS
  const open = Boolean(anchorEl);
  const isJoinButtonsEnabled =
    !authState.user ||
    (authState.user &&
      authState.user.role &&
      authState.user.role.name === 'Guest');

  // METHODS
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const platformLoginUrl = `${process.env.REACT_APP_TUTOPIYA_PLATFORM_BASE_URL}/#/login`;
  const onboardingLoginUrl = `${process.env.REACT_APP_TUTOPIYA_ONBOARDING_BASE_URL}`;
  const classBookingUrl = `${process.env.REACT_APP_TUTOPIYA_PLATFORM_BASE_URL}/#/dashboard/consultee/consultant`;
  const isTrialRecordingsInUrl = window.location.href.includes(
    'trial-recording'
  );

  return (
    <Box flexGrow={1}>
      <AppBar position="fixed" color="inherit">
        <Toolbar>
          <a href={`${process.env.PUBLIC_URL}/`}>
            <img
              src={`${process.env.PUBLIC_URL}/images/logos/tp-logo-text-green.png`}
              alt="tp-logo"
              style={{ width: '150px' }}
            />
          </a>

          {!isMobile && (
            <>
              <Divider {...dividerProps} />

              <Typography variant="h6" color={TP_COLORS.blue.light}>
                Learning Platform
              </Typography>
            </>
          )}

          <div style={{ flexGrow: 1 }} />

          {/* MOBILE OPTIONS BUTTON */}
          {isJoinButtonsEnabled && (
            <IconButton
              id="mobile-landing-header-main-routes-button"
              aria-controls={
                open ? 'mobile-landing-header-main-routes-menu' : undefined
              }
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              size="large"
              edge="start"
              aria-label="menu"
              sx={{ mr: 2, display: { xs: 'block', lg: 'none' } }}
              onClick={handleClick}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* DESKTOP OPTIONS */}
          {isJoinButtonsEnabled && (
            <Stack
              direction="row"
              alignItems="center"
              sx={{ display: { xs: 'none', lg: 'flex' } }}
            >
              <div>
                <Button
                  disableElevation
                  variant="contained"
                  color="inherit"
                  href={platformLoginUrl}
                  target="_blank"
                  style={{ color: TP_COLORS.black, marginRight: '10px' }}
                >
                  Sign in
                </Button>
              </div>
              {!isTrialRecordingsInUrl && (
                <div>
                  <Button
                    disableElevation
                    variant="contained"
                    color="primary"
                    href={onboardingLoginUrl}
                    target="_blank"
                  >
                    Sign Up
                  </Button>
                </div>
              )}
              {isTrialRecordingsInUrl && (
                <div>
                  <Button
                    disableElevation
                    variant="contained"
                    color="primary"
                    href={classBookingUrl}
                    target="_blank"
                    startIcon={<CalendarTodayIcon />}
                  >
                    Book Classes With Tutopiya
                  </Button>
                </div>
              )}
            </Stack>
          )}
        </Toolbar>
      </AppBar>

      {/* MENU */}
      <Menu
        id="mobile-landing-header-main-routes-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{ style: { borderRadius: '12px', minWidth: '300px' } }}
        MenuListProps={{
          'aria-labelledby': 'mobile-landing-header-main-routes-button',
          style: { padding: '15px' },
        }}
      >
        <MenuItem
          component="a"
          href={platformLoginUrl}
          target="_blank"
          style={{ padding: '15px', borderRadius: '7px' }}
          onClick={handleClose}
        >
          <ListItemIcon>
            <LoginIcon />
          </ListItemIcon>
          <ListItemText primary="Sign in" />
        </MenuItem>
        {!isTrialRecordingsInUrl && (
          <MenuItem
            component="a"
            href={onboardingLoginUrl}
            target="_blank"
            sx={{
              padding: '15px',
              borderRadius: '7px',
              backgroundColor: 'primary.main',
              color: TP_COLORS.white,
              '&:focus': {
                backgroundColor: 'primary.main',
              },
              '&:hover': {
                backgroundColor: 'primary.main',
              },
            }}
            color="primary"
          >
            <ListItemIcon>
              <CalendarTodayIcon sx={{ color: TP_COLORS.white }} />
            </ListItemIcon>
            <ListItemText primary="Sign Up" />
          </MenuItem>
        )}
        {isTrialRecordingsInUrl && (
          <MenuItem
            component="a"
            href={classBookingUrl}
            target="_blank"
            sx={{
              padding: '15px',
              borderRadius: '7px',
              backgroundColor: 'primary.main',
              color: TP_COLORS.white,
              '&:focus': {
                backgroundColor: 'primary.main',
              },
              '&:hover': {
                backgroundColor: 'primary.main',
              },
            }}
            color="primary"
          >
            <ListItemIcon>
              <CalendarTodayIcon sx={{ color: TP_COLORS.white }} />
            </ListItemIcon>
            <ListItemText primary="Book Classes With Tutopiya" />
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default HomeHeader;
